import { useState, useMemo } from 'react'
import { Stack, Button, Box, TextField, InputLabel } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { useTranslation } from 'react-i18next';

export interface ICreateAircraftDialog {
    open : boolean;
    airlineList : {label: string, value: string}[];
    acTypeList : {label: string, value: string}[];
    existingRegistrations : string[];
    cancelAction : () => void;
    createAction : (airlineCode : string, code : string, acType : string) => void;
}

function AircraftCreateDialog(props : ICreateAircraftDialog) {
    const { t } = useTranslation();
    
    const [ code, setCode] = useState('');
    const [ type, setType] = useState('');
    const [ airlineCode, setAirlineCode] = useState('');

    const [ codeError, setCodeError] = useState(true);
    const [ typeError, setTypeError] = useState(true);
    const [ airlineCodeError, setAirlineCodeError] = useState(true);

    const handleCode = (e : React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        let value = e.target.value;
        setCode(value);
        setCodeError(value === undefined || value === '');
    }

    const handleType = (value : string) => {
        setType(value ?? '');
        setTypeError(value === undefined || value === '');
    }

    const handleAirlineCode = (value : string | undefined) => {
        setAirlineCode(value ?? '');
        setAirlineCodeError(value === undefined || value === '');
    }

    const duplicateError = useMemo(() => {
        if(!code) return false;
        return props.existingRegistrations.includes(code.toUpperCase());
    }, [code, props.existingRegistrations]);

    const hasErrors = useMemo(() => {
        return codeError || typeError || duplicateError;
    }, [codeError, typeError, duplicateError]);

    return (
        <div>
        <Dialog open={props.open} onClose={props.cancelAction}>
            <DialogTitle>Create Aicraft</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Please fill out the form to create a new aircraft.
                </DialogContentText>
                <Box>
                <Stack spacing={2} direction="column" sx={{ marginBottom: 4, marginTop: 1, paddingTop:2 }}>
                    <FormControl sx={{minWidth: 200 }} size="small">
                        <InputLabel id="airline-label">Airline</InputLabel>
                        <Select
                            labelId="airline-label"
                            id="airline-select"
                            value={airlineCode}
                            error={airlineCodeError}
                            label="Type"
                            onChange={(e) => handleAirlineCode(e.target.value) }>
                                { props.airlineList && props.airlineList.map((o)=> (
                                    <MenuItem  key={o.value} value={o.value}>{o.label}</MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                    <FormControl sx={{minWidth: 200 }} size="small">
                        <TextField
                                        type="text"
                                        variant='outlined'
                                        color='secondary'
                                        label={t('Aicraft Registration')}
                                        onChange={e => handleCode(e)}
                                        value={code}
                                        error={codeError || duplicateError}
                                        helperText={codeError ? 'Please enter a registration (e.g. OO-JAS)' : duplicateError ? 'This registration already exists' : ''}
                                        fullWidth
                                        size="small"
                            />
                    </FormControl>
                    <FormControl sx={{minWidth: 200 }} size="small">
                        <InputLabel id="acType-label">Aircraft Type</InputLabel>
                        <Select
                            labelId="acType-label"
                            id="acType-select"
                            value={type}
                            error={typeError}
                            label="Type"
                            onChange={(e) => handleType(e.target.value) }>
                                { props.acTypeList && props.acTypeList.map((o)=> (
                                    <MenuItem  key={o.value} value={o.value}>{o.label}</MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                </Stack>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={props.cancelAction}>Cancel</Button>
                <Button disabled={hasErrors} onClick={(e) => props.createAction(airlineCode, code.toUpperCase(), type)}>Create</Button>
            </DialogActions>
      </Dialog>
      </div>
    )
}

export default AircraftCreateDialog;